import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { CalendarDays, ChevronLeft, ChevronRight, Clock, Minus, Plus } from "lucide-react";

// Global date + time picker primitive — a calendar month grid plus an
// hour/minute stepper, portalled to <body> (same pattern as SearchableSelect
// / StepModal) so it's never clipped by a modal's overflow-hidden shell.
//
// `value`/`onChange` deal in plain JS Date objects used purely as a wall-clock
// container (year/month/day/hour/minute read via the local Date accessors) —
// this component has no timezone awareness of its own. Callers that need the
// selection interpreted in a specific account timezone (rather than the
// browser's) should do that conversion themselves, e.g. via
// `zonedDateTimeToUtc` in `services/date-converter.js`.
//
// Usage:
//   <DateTimePicker value={date} onChange={setDate} minDate={new Date()} timeFormat="24" />
//
// `defaultTime` ({ hour, minute }) is the time given to a day picked while no
// value is set; without it the current time (or minDate's) is used. A range
// "To" picker passes { hour: 23, minute: 59 } so picking only a day covers it whole.
//
// `variant="select"` sizes the trigger like SearchableSelect, for forms that mix both.
const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAY_LABELS = ["S", "M", "T", "W", "T", "F", "S"];
const MINUTE_STEP = 5;

const pad2 = (n) => String(n).padStart(2, "0");
const startOfDay = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
const sameDay = (a, b) => !!a && !!b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();

const formatTrigger = (date, timeFormat, showTime) => {
  if (!date) return "";
  const datePart = `${MONTH_NAMES[date.getMonth()].slice(0, 3)} ${date.getDate()}, ${date.getFullYear()}`;
  if (!showTime) return datePart;
  const h24 = date.getHours();
  const m = pad2(date.getMinutes());
  const timePart =
    timeFormat === "12"
      ? `${(() => { const h = h24 % 12 || 12; return h; })()}:${m} ${h24 < 12 ? "AM" : "PM"}`
      : `${pad2(h24)}:${m}`;
  return `${datePart}, ${timePart}`;
};

const buildCalendarCells = (viewYear, viewMonth) => {
  const startWeekday = new Date(viewYear, viewMonth, 1).getDay();
  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const cells = new Array(startWeekday).fill(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);
  return cells;
};

export default function DateTimePicker({
  value,
  onChange,
  minDate = null,
  maxDate = null,
  timeFormat = "24",
  showTime = true,
  placeholder = "Select date & time",
  hasError = false,
  isDisabled = false,
  defaultTime = null,
  variant = "default",
}) {
  const seed = value || minDate || new Date();
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState(null);
  const [viewYear, setViewYear] = useState(seed.getFullYear());
  const [viewMonth, setViewMonth] = useState(seed.getMonth());
  const [hour, setHour] = useState(!value && defaultTime ? defaultTime.hour : seed.getHours());
  const [minute, setMinute] = useState(!value && defaultTime ? defaultTime.minute : seed.getMinutes());

  const triggerRef = useRef(null);
  const panelRef = useRef(null);

  const defaultHour = defaultTime?.hour;
  const defaultMinute = defaultTime?.minute;

  useEffect(() => {
    if (!value) {
      // Cleared: the next picked day starts from the default time again.
      if (defaultHour !== undefined) {
        setHour(defaultHour);
        setMinute(defaultMinute);
      }
      return;
    }
    setViewYear(value.getFullYear());
    setViewMonth(value.getMonth());
    setHour(value.getHours());
    setMinute(value.getMinutes());
  }, [value, defaultHour, defaultMinute]);

  const reposition = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) setCoords({ top: rect.bottom + 6, left: rect.left, width: rect.width, triggerTop: rect.top, triggerBottom: rect.bottom });
  };

  const openPanel = () => {
    if (isDisabled) return;
    reposition();
    setIsOpen(true);
  };

  useEffect(() => {
    if (!isOpen) return;
    const handleOutside = (e) => {
      if (panelRef.current?.contains(e.target) || triggerRef.current?.contains(e.target)) return;
      setIsOpen(false);
    };
    document.addEventListener("mousedown", handleOutside);
    window.addEventListener("scroll", reposition, true);
    window.addEventListener("resize", reposition);
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      window.removeEventListener("scroll", reposition, true);
      window.removeEventListener("resize", reposition);
    };
  }, [isOpen]);

  // Keeps the floating panel fully on-screen: flips it above the trigger
  // when there isn't room below (but there is above), and otherwise clamps
  // it so its bottom/right edge never runs past the viewport. Runs via
  // direct style mutation (not state) so it settles before paint — no
  // visible jump from an intermediate "wrong" position.
  useLayoutEffect(() => {
    if (!isOpen || !coords || !panelRef.current) return;
    const panelEl = panelRef.current;
    const panelRect = panelEl.getBoundingClientRect();
    const viewportH = window.innerHeight;
    const viewportW = window.innerWidth;
    const margin = 8;

    const spaceBelow = viewportH - coords.triggerBottom;
    const spaceAbove = coords.triggerTop;
    let top;
    if (panelRect.height + margin > spaceBelow && spaceAbove > spaceBelow) {
      top = Math.max(margin, coords.triggerTop - panelRect.height - 6);
    } else {
      top = Math.min(coords.top, Math.max(margin, viewportH - panelRect.height - margin));
    }
    panelEl.style.top = `${top}px`;

    const maxLeft = Math.max(margin, viewportW - panelRect.width - margin);
    panelEl.style.left = `${Math.min(coords.left, maxLeft)}px`;
  }, [isOpen, coords]);

  const minDay = minDate ? startOfDay(minDate) : null;
  const maxDay = maxDate ? startOfDay(maxDate) : null;

  const isDayDisabled = (day) => {
    if (day == null) return false;
    const candidate = new Date(viewYear, viewMonth, day);
    if (minDay && candidate < minDay) return true;
    if (maxDay && candidate > maxDay) return true;
    return false;
  };

  const clampToRange = (candidate) => {
    if (minDate && candidate < minDate) return minDate;
    if (maxDate && candidate > maxDate) return maxDate;
    return candidate;
  };

  const emitDay = (day) => {
    if (day == null || isDayDisabled(day)) return;
    const candidate = clampToRange(new Date(viewYear, viewMonth, day, hour, minute));
    setHour(candidate.getHours());
    setMinute(candidate.getMinutes());
    onChange(candidate);
    if (!showTime) setIsOpen(false);
  };

  const emitTime = (nextHour, nextMinute) => {
    setHour(nextHour);
    setMinute(nextMinute);
    if (!value) return; // no day chosen yet — just remember the time for later
    onChange(clampToRange(new Date(viewYear, viewMonth, value.getDate(), nextHour, nextMinute)));
  };

  const stepHour = (delta) => emitTime((hour + delta + 24) % 24, minute);
  const stepMinute = (delta) => emitTime(hour, (minute + delta + 60) % 60);

  const goToPrevMonth = () => {
    const d = new Date(viewYear, viewMonth - 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };
  const goToNextMonth = () => {
    const d = new Date(viewYear, viewMonth + 1, 1);
    setViewYear(d.getFullYear());
    setViewMonth(d.getMonth());
  };

  const displayHour = timeFormat === "12" ? hour % 12 || 12 : hour;
  const meridiem = hour < 12 ? "AM" : "PM";
  const setMeridiem = (next) => {
    if (next === meridiem) return;
    const nextHour = next === "AM" ? hour - 12 : hour + 12;
    emitTime((nextHour + 24) % 24, minute);
  };

  const cells = buildCalendarCells(viewYear, viewMonth);

  return (
    <>
      <button
        type="button"
        ref={triggerRef}
        onClick={openPanel}
        disabled={isDisabled}
        className={`w-full flex items-center gap-2 px-3 text-left disabled:opacity-60 disabled:cursor-not-allowed ${
          variant === "select" ? "h-[38px] text-[13px] rounded border bg-surface" : "py-2 text-sm rounded-lg border bg-surface_alt"
        } ${
          hasError ? "border-danger" : "border-border"
        }`}
      >
        <CalendarDays size={15} className="text-text_muted flex-shrink-0" />
        <span className={value ? "text-text_primary" : "text-text_muted"}>
          {value ? formatTrigger(value, timeFormat, showTime) : placeholder}
        </span>
      </button>

      {isOpen &&
        coords &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              minWidth: Math.max(coords.width, 280),
              maxHeight: "calc(100vh - 16px)",
              overflowY: "auto",
              zIndex: 9999,
            }}
            className="rounded-xl border border-border bg-surface shadow-2xl p-3"
          >
            <div className="flex items-center justify-between mb-2">
              <button type="button" onClick={goToPrevMonth} className="p-1 rounded-md text-text_secondary hover:bg-surface_alt">
                <ChevronLeft size={15} />
              </button>
              <span className="text-xs font-semibold text-text_primary">
                {MONTH_NAMES[viewMonth]} {viewYear}
              </span>
              <button type="button" onClick={goToNextMonth} className="p-1 rounded-md text-text_secondary hover:bg-surface_alt">
                <ChevronRight size={15} />
              </button>
            </div>

            <div className="grid grid-cols-7 gap-1 mb-1">
              {WEEKDAY_LABELS.map((w, i) => (
                <div key={`${w}-${i}`} className="text-center text-[10px] font-medium text-text_muted py-1">
                  {w}
                </div>
              ))}
            </div>

            <div className="grid grid-cols-7 gap-1">
              {cells.map((day, idx) => {
                if (day == null) return <div key={`empty-${idx}`} />;
                const disabled = isDayDisabled(day);
                const selected = value && sameDay(value, new Date(viewYear, viewMonth, day));
                const isToday = sameDay(new Date(), new Date(viewYear, viewMonth, day));
                return (
                  <button
                    type="button"
                    key={day}
                    disabled={disabled}
                    onClick={() => emitDay(day)}
                    className={`h-7 w-7 rounded-lg text-xs font-medium flex items-center justify-center ${
                      selected
                        ? "bg-primary text-white"
                        : disabled
                        ? "text-text_muted opacity-40 cursor-not-allowed"
                        : isToday
                        ? "text-primary border border-primary/40 hover:bg-surface_alt"
                        : "text-text_primary hover:bg-surface_alt"
                    }`}
                  >
                    {day}
                  </button>
                );
              })}
            </div>

            {showTime && (
              <div className="flex items-center justify-between gap-2 mt-3 pt-3 border-t border-border">
                <div className="flex items-center gap-1.5 text-text_muted">
                  <Clock size={13} />
                </div>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => stepHour(-1)} className="p-1 rounded-md border border-border text-text_secondary hover:bg-surface_alt">
                    <Minus size={11} />
                  </button>
                  <span className="w-6 text-center text-xs font-semibold tabular-nums text-text_primary">{pad2(displayHour)}</span>
                  <button type="button" onClick={() => stepHour(1)} className="p-1 rounded-md border border-border text-text_secondary hover:bg-surface_alt">
                    <Plus size={11} />
                  </button>
                </div>
                <span className="text-xs font-semibold text-text_muted">:</span>
                <div className="flex items-center gap-1">
                  <button type="button" onClick={() => stepMinute(-MINUTE_STEP)} className="p-1 rounded-md border border-border text-text_secondary hover:bg-surface_alt">
                    <Minus size={11} />
                  </button>
                  <span className="w-6 text-center text-xs font-semibold tabular-nums text-text_primary">{pad2(minute)}</span>
                  <button type="button" onClick={() => stepMinute(MINUTE_STEP)} className="p-1 rounded-md border border-border text-text_secondary hover:bg-surface_alt">
                    <Plus size={11} />
                  </button>
                </div>
                {timeFormat === "12" && (
                  <div className="flex items-center rounded-md border border-border overflow-hidden">
                    {["AM", "PM"].map((m) => (
                      <button
                        type="button"
                        key={m}
                        onClick={() => setMeridiem(m)}
                        className={`px-1.5 py-1 text-[10px] font-semibold ${
                          meridiem === m ? "bg-primary text-white" : "text-text_secondary hover:bg-surface_alt"
                        }`}
                      >
                        {m}
                      </button>
                    ))}
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => setIsOpen(false)}
                  className="ml-auto px-2.5 py-1 text-[11px] font-semibold rounded-md bg-primary text-white"
                >
                  Done
                </button>
              </div>
            )}
          </div>,
          document.body
        )}
    </>
  );
}
